import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { isAPIRequest, toBoolean } from '@epicuro-next/utilities';
import { Observable, switchMap, take } from 'rxjs';

import { IAuthInterceptorConfiguration } from '../models/interceptor-configuration.interface';
import { AuthenticationService } from '../services/authentication.service';
import { AUTH_INTERCEPTOR_CONFIG } from '../tokens';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    @Inject(AUTH_INTERCEPTOR_CONFIG)
    private config: IAuthInterceptorConfiguration,
    private authService: AuthenticationService,
  ) {}

  public intercept(
    httpRequest: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const hostName = window.location.hostname;
    if (
      !isAPIRequest(httpRequest, hostName) ||
      this.isUnprotected(httpRequest)
    ) {
      return next.handle(httpRequest);
    }

    const token = this.authService.accessToken;

    if (!!token) {
      return this.attachTokenAndProceed(httpRequest, next, token as string);
    }

    return this.acquireToken(httpRequest, next);
  }

  private attachTokenAndProceed(
    httpRequest: HttpRequest<unknown>,
    next: HttpHandler,
    token: string,
  ): Observable<HttpEvent<unknown>> {
    const httpRequestClone = httpRequest.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });

    return next.handle(httpRequestClone);
  }

  private acquireToken(httpRequest: HttpRequest<unknown>, next: HttpHandler) {
    return this.authService.acquireTokenSilent().pipe(
      take(1),
      switchMap((value) => {
        return this.attachTokenAndProceed(httpRequest, next, value.accessToken);
      }),
    );
  }

  private isUnprotected(request: HttpRequest<unknown>): boolean {
    if (request.headers.has('epc-protected')) {
      return !toBoolean(request.headers.get('epc-protected') as string);
    }

    return this.config.unprotectedResources.some((item) =>
      request.url.includes(item),
    );
  }
}
